/**
 * Game events (§4.3). Every action is an immutable, append-only event with an increasing seq.
 */
import type { FoulDetails } from '../fouls/fouls';
import type { MatchDetails, MatchInfo } from '../match/match';
import type { BreakKind, RuleSet } from '../rules/ruleset';
import type { EventSource, JerseyKind, PerSide, ScorePoints, TeamSide, Teams } from '../types';

/**
 * v1: initial events. v2: TEAM_FOUL details, SCORE.freeThrow/playerNumber, FREE_THROW_MISSED,
 * optional groupId. v3: POSSESSION_SET, BREAK_* events, SCORE.andOne/lastFreeThrow, rule
 * fields shotClock.resetOnScore and breaks, GAME_INFO_UPDATED, TeamInfo.kit, SHOT_CLOCK_STARTED, CLOCK_RESUME_HELD, REBOUND. v4: TeamInfo.roster, LINEUP_SET, SUBSTITUTION, PLAYER_REMOVED. All additions are optional, so older events
 * remain valid as-is.
 */
/** Why a player came off the court. */
export type SubstitutionReason = 'free' | 'injury' | 'foulOut';

export const SCHEMA_VERSION = 4;

export interface EventMeta {
  /** Globally unique id (UUID in the app). */
  id: string;
  gameId: string;
  /** Strictly increasing per game, starting at 1. */
  seq: number;
  /** Period the event belongs to (0 = before the first period). */
  periodNo: number;
  /** Game-clock remaining (ms) at the moment of the event. */
  clockMs: number;
  /** Wall-clock timestamp (ms since epoch) from the injected `now()`. */
  at: number;
  schemaVersion: number;
  source: EventSource;
  /** Events created together by one command share a groupId (e.g. and-one foul + SCORE). UNDO voids the whole group. */
  groupId?: string;
}

interface Ev<T extends string, P> extends EventMeta {
  type: T;
  payload: P;
}

/** match: the visible match number and tournament details (v3; missing in older games). */
export type GameCreatedEvent = Ev<
  'GAME_CREATED',
  { rules: RuleSet; teams: Teams; match?: MatchInfo }
>;
export type PeriodStartedEvent = Ev<'PERIOD_STARTED', { periodNo: number }>;
export type PeriodEndedEvent = Ev<'PERIOD_ENDED', { periodNo: number }>;
/** remainingMs = game clock when it started running (equals clockMs). */
export type ClockStartedEvent = Ev<'CLOCK_STARTED', { remainingMs: number }>;
/** remainingMs = game clock when it stopped (equals clockMs). */
export type ClockStoppedEvent = Ev<'CLOCK_STOPPED', { remainingMs: number }>;
export type ClockAdjustedEvent = Ev<'CLOCK_ADJUSTED', { remainingMs: number; previousMs: number }>;
export type ScoreEvent = Ev<
  'SCORE',
  {
    team: TeamSide;
    points: ScorePoints;
    playerId?: string;
    playerNumber?: string;
    /** true when the point came from a FREE_THROW command. */
    freeThrow?: boolean;
    /** true for the last free throw of a trip (resets the shot clock when the rule is on). */
    lastFreeThrow?: boolean;
    /** true for the basket of an and-one (free throw still to come: no shot-clock reset). */
    andOne?: boolean;
  }
>;
/** team = the team that committed the foul. */
export type TeamFoulEvent = Ev<
  'TEAM_FOUL',
  {
    team: TeamSide;
    playerId?: string;
    /** This foul stopped a running game clock, so the clock waits to resume (§5.2). */
    stoppedClock?: boolean;
  } & FoulDetails
>;
/** A missed free throw by `team` (made ones are SCORE events with freeThrow: true). */
export type FreeThrowMissedEvent = Ev<
  'FREE_THROW_MISSED',
  { team: TeamSide; playerNumber?: string }
>;
export type TimeoutEvent = Ev<'TIMEOUT', { team: TeamSide }>;
/**
 * seconds = the actual value the shot clock was reset to (rules.shotClock.fullSec or resetSec;
 * 24 or 14 for the FIBA/NBA presets).
 */
export type ShotClockResetEvent = Ev<'SHOT_CLOCK_RESET', { seconds: number }>;
/** Sets who has the ball (`team`) and/or the alternating-possession arrow (`arrow`). */
export type PossessionSetEvent = Ev<'POSSESSION_SET', { team?: TeamSide; arrow?: TeamSide }>;
/** A timeout or between-period break timer starts, running from `remainingMs`. */
export type BreakStartedEvent = Ev<'BREAK_STARTED', { kind: BreakKind; remainingMs: number }>;
/** remainingMs = break time left at that moment. */
export type BreakPausedEvent = Ev<'BREAK_PAUSED', { remainingMs: number }>;
export type BreakResumedEvent = Ev<'BREAK_RESUMED', { remainingMs: number }>;
/** The operator set the break timer to `remainingMs` (keeps it running or paused). */
export type BreakAdjustedEvent = Ev<'BREAK_ADJUSTED', { remainingMs: number }>;
export type BreakEndedEvent = Ev<'BREAK_ENDED', { remainingMs: number }>;
/** Jersey colour worn by a team in this game. */
export interface JerseyUpdate {
  color: string;
  kit?: JerseyKind;
}
/**
 * Game details changed after creation (§5.1): jerseys, court, venue and officials.
 * Not undoable: the operator changes it back with the same sheet.
 */
export type GameInfoUpdatedEvent = Ev<
  'GAME_INFO_UPDATED',
  { jerseys?: Partial<PerSide<JerseyUpdate>>; details?: MatchDetails }
>;
/** offensive = the team that had the ball got it back (shot clock 14), otherwise 24. */
export type ReboundEvent = Ev<'REBOUND', { team: TeamSide; offensive: boolean }>;
/** The throw-in was touched: a held shot clock starts counting. */
export type ShotClockStartedEvent = Ev<'SHOT_CLOCK_STARTED', Record<string, never>>;
/** The scorer cancelled the automatic restart after a foul; the clock waits for a tap. */
export type ClockResumeHeldEvent = Ev<'CLOCK_RESUME_HELD', Record<string, never>>;
/**
 * Who is on court for `team`, chosen by the scorer. `forPeriod` marks them as the starting five of
 * that coming period.
 */
export type LineupSetEvent = Ev<
  'LINEUP_SET',
  { team: TeamSide; numbers: string[]; forPeriod?: number }
>;
/** A fouled-out player left the court with nobody to replace them. */
export type PlayerRemovedEvent = Ev<'PLAYER_REMOVED', { team: TeamSide; number: string }>;
/** `in` came on for `out`. See core/lineup for what `reason` means to the playing rule. */
export type SubstitutionEvent = Ev<
  'SUBSTITUTION',
  { team: TeamSide; out: string; in: string; reason: SubstitutionReason }
>;
export type EventVoidedEvent = Ev<'EVENT_VOIDED', { targetId: string }>;
export type GameEndedEvent = Ev<'GAME_ENDED', Record<string, never>>;

export type GameEvent =
  | GameCreatedEvent
  | PeriodStartedEvent
  | PeriodEndedEvent
  | ClockStartedEvent
  | ClockStoppedEvent
  | ClockAdjustedEvent
  | ScoreEvent
  | TeamFoulEvent
  | FreeThrowMissedEvent
  | TimeoutEvent
  | ShotClockResetEvent
  | PossessionSetEvent
  | BreakStartedEvent
  | BreakPausedEvent
  | BreakResumedEvent
  | BreakAdjustedEvent
  | BreakEndedEvent
  | GameInfoUpdatedEvent
  | ShotClockStartedEvent
  | ReboundEvent
  | ClockResumeHeldEvent
  | LineupSetEvent
  | SubstitutionEvent
  | PlayerRemovedEvent
  | EventVoidedEvent
  | GameEndedEvent;

export type GameEventType = GameEvent['type'];
export type EventOfType<T extends GameEventType> = Extract<GameEvent, { type: T }>;
export type PayloadOf<T extends GameEventType> = EventOfType<T>['payload'];

export const GAME_EVENT_TYPES: readonly GameEventType[] = [
  'GAME_CREATED',
  'PERIOD_STARTED',
  'PERIOD_ENDED',
  'CLOCK_STARTED',
  'CLOCK_STOPPED',
  'CLOCK_ADJUSTED',
  'SCORE',
  'TEAM_FOUL',
  'FREE_THROW_MISSED',
  'TIMEOUT',
  'SHOT_CLOCK_RESET',
  'POSSESSION_SET',
  'BREAK_STARTED',
  'BREAK_PAUSED',
  'BREAK_RESUMED',
  'BREAK_ADJUSTED',
  'BREAK_ENDED',
  'GAME_INFO_UPDATED',
  'SHOT_CLOCK_STARTED',
  'CLOCK_RESUME_HELD',
  'REBOUND',
  'LINEUP_SET',
  'SUBSTITUTION',
  'PLAYER_REMOVED',
  'EVENT_VOIDED',
  'GAME_ENDED',
];

/** Event types that UNDO may void. */
export const UNDOABLE_EVENT_TYPES: readonly GameEventType[] = [
  'SCORE',
  'TEAM_FOUL',
  'FREE_THROW_MISSED',
  'TIMEOUT',
  'SHOT_CLOCK_RESET',
  'POSSESSION_SET',
  'REBOUND',
  'LINEUP_SET',
  'SUBSTITUTION',
  'PLAYER_REMOVED',
];
